import { AdvancedMarker, Map, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import {
  DEFAULT_MAP_CENTER,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_MAP_ID,
  hasLocationCoordinates,
  parseCoord,
} from '@/lib/google-maps';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';
import { MAP_CONTAINER_STYLE } from './interactive-map/constants';
import { MapRefAttacher } from './interactive-map/MapRefAttacher';
import { PlacedMapPin } from './interactive-map/PlacedMapPin';
import type { InteractiveMapProps } from './interactive-map/types';
import { useMapDragInteractions } from './interactive-map/useMapDragInteractions';
import { useMapLocation } from './interactive-map/useMapLocation';
import { useMapViewport } from './interactive-map/useMapViewport';
import { usePlacesAutocomplete } from './interactive-map/usePlacesAutocomplete';
import { getResolvedHint } from './interactive-map/utils';

export function InteractiveMap(props: InteractiveMapProps) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const rootClassName = `interactive-map ${props.className ?? ''}`.trim();

  if (loadError) {
    return (
      <div className={rootClassName}>
        <div style={{ ...MAP_CONTAINER_STYLE, padding: '1rem' }}>
          Map could not be loaded. Check your Google Maps API key and network
          connection.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={rootClassName}>
        <div style={MAP_CONTAINER_STYLE} />
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY?.trim()) {
    return (
      <div className={rootClassName}>
        <div style={{ ...MAP_CONTAINER_STYLE, padding: '1rem' }}>
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in your environment to
          enable the map.
        </div>
      </div>
    );
  }

  return <InteractiveMapContent {...props} rootClassName={rootClassName} />;
}

interface InteractiveMapContentProps extends InteractiveMapProps {
  rootClassName: string;
}

function MapPlacedMarker({
  show,
  position,
  readOnly,
  isDraggingMarker,
  onDragStart,
  onDrag,
  onDragEnd,
}: {
  show: boolean;
  position: google.maps.LatLngLiteral;
  readOnly: boolean;
  isDraggingMarker: boolean;
  onDragStart: (event: google.maps.MapMouseEvent) => void;
  onDrag: (event: google.maps.MapMouseEvent) => void;
  onDragEnd: (event: google.maps.MapMouseEvent) => void;
}) {
  const markerLibrary = useMapsLibrary('marker');

  if (!show || !markerLibrary) {
    return null;
  }

  return (
    <AdvancedMarker
      position={position}
      draggable={!readOnly}
      className={
        isDraggingMarker ? 'interactive-map-placed-marker--dragging' : undefined
      }
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    >
      <PlacedMapPin />
    </AdvancedMarker>
  );
}

function InteractiveMapContent({
  location,
  locationName = '',
  onLocationChange,
  onLocationNameChange,
  readOnly = false,
  showSearch = false,
  geocodeOnPlace = false,
  locationNameRequired = false,
  defaultCenter = DEFAULT_MAP_CENTER,
  defaultZoom,
  hintText,
  rootClassName,
}: InteractiveMapContentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const paletteButtonRef = useRef<HTMLButtonElement>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const markerCenter = useMemo(
    () => ({
      lat: parseCoord(location?.latitude, defaultCenter.lat),
      lng: parseCoord(location?.longitude, defaultCenter.lng),
    }),
    [
      location?.latitude,
      location?.longitude,
      defaultCenter.lat,
      defaultCenter.lng,
    ],
  );

  const hasCoordinates = hasLocationCoordinates(location);
  const canDragFromPalette = !readOnly && !hasCoordinates;

  const {
    mapCenter,
    mapZoom,
    showMapMarker,
    setShowMapMarker,
    handleMapReady,
    handleCameraChanged,
    captureMapViewport,
  } = useMapViewport({
    defaultCenter,
    defaultZoom,
    hasCoordinates,
    markerCenter,
    mapRef,
  });

  const {
    isGeocoding,
    clearLocation,
    resolveCoordinates,
    setLocationRef,
    handlePlaceChanged,
  } = useMapLocation({
    geocodeOnPlace,
    showSearch,
    onLocationChange,
    onLocationNameChange,
    captureMapViewport,
    setShowMapMarker,
    inputRef,
  });

  useEffect(() => {
    setLocationRef(location);
  }, [location, setLocationRef]);

  usePlacesAutocomplete({
    inputRef,
    isLoaded: true,
    readOnly,
    showSearch,
    locationName,
    onPlaceChanged: handlePlaceChanged,
  });

  const {
    isDragging,
    dragPosition,
    isDraggingMarker,
    markerDragPosition,
    isOverMap,
    isOverPalette,
    handleMarkerDragStart,
    handleMarkerDrag,
    handleMarkerDragEnd,
    handlePalettePointerDown,
    handlePalettePointerUp,
    handlePalettePointerCancel,
  } = useMapDragInteractions({
    mapRef,
    mapWrapperRef,
    paletteRef,
    paletteButtonRef,
    readOnly,
    canDragFromPalette,
    showMapMarker,
    resolveCoordinates,
    clearLocation,
  });

  const handleMapReadyWithState = useCallback(
    (map: google.maps.Map) => {
      handleMapReady(map);
      setIsMapReady(true);
    },
    [handleMapReady],
  );

  useEffect(() => {
    return () => {
      setIsMapReady(false);
    };
  }, []);

  const resolvedHint = getResolvedHint({ hintText, readOnly, showSearch });

  const ghostPosition =
    isDraggingMarker && markerDragPosition
      ? markerDragPosition
      : isDragging && dragPosition
        ? dragPosition
        : null;

  return (
    <div className={rootClassName}>
      {showSearch ? (
        <Input
          ref={inputRef}
          type="text"
          defaultValue={locationName}
          placeholder="Enter address or venue name..."
          onChange={(event) => onLocationNameChange?.(event.target.value)}
          disabled={readOnly}
          required={locationNameRequired && !readOnly}
          autoComplete="off"
          aria-busy={isGeocoding}
        />
      ) : null}
      {showSearch || !readOnly ? (
        <p className="interactive-map-hint">{resolvedHint}</p>
      ) : null}
      <div
        ref={mapWrapperRef}
        className={`interactive-map-wrapper${isOverMap && isDragging ? ' interactive-map-wrapper--drop-target' : ''}`}
      >
        <Map
          style={MAP_CONTAINER_STYLE}
          mapId={GOOGLE_MAPS_MAP_ID}
          zoom={mapZoom}
          center={mapCenter}
          clickableIcons={!readOnly}
          streetViewControl={false}
          onCameraChanged={handleCameraChanged}
        >
          <MapRefAttacher
            mapRef={mapRef}
            onMapReady={handleMapReadyWithState}
          />
          <MapPlacedMarker
            show={isMapReady && showMapMarker && hasCoordinates}
            position={markerCenter}
            readOnly={readOnly}
            isDraggingMarker={isDraggingMarker}
            onDragStart={handleMarkerDragStart}
            onDrag={handleMarkerDrag}
            onDragEnd={handleMarkerDragEnd}
          />
        </Map>
        {!readOnly ? (
          <div
            ref={paletteRef}
            className={`interactive-map-palette${isOverPalette ? ' interactive-map-palette--drop-target' : ''}`}
            aria-label="Drop pin here to clear location"
          >
            <button
              ref={paletteButtonRef}
              type="button"
              className={`interactive-map-palette-pin${canDragFromPalette ? '' : ' interactive-map-palette-pin--inactive'}`}
              aria-label="Drag to place location on map"
              aria-hidden={!canDragFromPalette && !isDragging}
              tabIndex={canDragFromPalette ? 0 : -1}
              onPointerDown={handlePalettePointerDown}
              onPointerUp={handlePalettePointerUp}
              onPointerCancel={handlePalettePointerCancel}
            >
              {canDragFromPalette && !isDragging ? (
                <MapPin aria-hidden="true" />
              ) : null}
            </button>
          </div>
        ) : null}
      </div>
      {ghostPosition
        ? createPortal(
            <div
              className={`interactive-map-drag-ghost${
                isDraggingMarker
                  ? isOverPalette
                    ? ' interactive-map-drag-ghost--over-palette'
                    : ''
                  : isOverMap
                    ? ' interactive-map-drag-ghost--over-map'
                    : ''
              }`}
              style={{
                left: ghostPosition.x,
                top: ghostPosition.y,
              }}
              aria-hidden="true"
            >
              <MapPin />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export type { InteractiveMapProps } from './interactive-map/types';
